import { Box, Paper } from '@mui/material';
import React, { useMemo } from 'react';
import { useOCRAnalysis } from '../../hooks/useOCRAnalysis';
import { AnnotationModal } from '../AnnotationModal';
import { CanvasComponent } from '../CanvasComponent';
import { DocumentPreview } from './DocumentPreview';

interface AnnotationPreviewProps {
  annotation: any;
  tables: Record<string, any>;
  index: number;
  onDelete: (id: string) => void;
}

const AnnotationPreview: React.FC<AnnotationPreviewProps> = React.memo(({
  annotation,
  tables,
  index,
  onDelete
}) => {
  const combinedTableData = useMemo(() => {
    const relatedTables = Object.values(tables).filter(
      table => table.annotationId === annotation.id
    );

    if (relatedTables.length === 0) return undefined;

    return {
      ...relatedTables[0],
      grid: relatedTables.flatMap(table => table.grid),
    };
  }, [tables, annotation.id]);

  const handleDelete = () => onDelete(annotation.id);

  return (
    <div 
      key={annotation.id} 
      id={`annotation-preview-${annotation.id}`}
      role="region"
      aria-label={`Annotation ${index + 1}`}
    >
      <DocumentPreview
        annotationData={annotation}
        tableData={combinedTableData}
        title={`Annotation #${index + 1}`}
        onDelete={handleDelete}
      />
    </div>
  );
});

export const LeftPanelContainer: React.FC = () => {
  const {
    modalOpen,
    tables,
    selectedTemplate,
    annotationIds,
    annotations,
    pendingAnnotation,
    isAnalyzing,
    analysisError,
    handleAnnotationCreate,
    handleAnalyzeConfirm,
    handleModalClose,
    handleAnnotationDelete,
    retryAnalysis
  } = useOCRAnalysis();

  const validAnnotations = useMemo(() => 
    annotationIds
      .map(id => annotations[id])
      .filter(Boolean),
    [annotationIds, annotations]
  );

  if (!selectedTemplate) {
    return null;
  }

  return (
    <Box 
      height="100%" 
      component="section"
      aria-label="Document analysis panel"
    >
      <Paper
        sx={{
          border: theme => `0.5px solid ${theme.palette.Neutrals[200]}`,
          maxHeight: '100%',
          overflowY: 'auto',
          boxSizing: 'border-box',
          p: 2,
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: '2px'
          }
        }}
        elevation={0}
        tabIndex={-1}
      >
        <CanvasComponent
          imageUrl={selectedTemplate.url}
          isMainCanvas
          onAnnotationCreate={handleAnnotationCreate}
        />

        {validAnnotations.length > 0 && (
          <Box 
            component="section"
            aria-label={`${validAnnotations.length} annotation${validAnnotations.length === 1 ? '' : 's'}`}
            mt={2}
          >
            {validAnnotations.map((annotation, index) => (
              <AnnotationPreview
                key={annotation.id}
                annotation={annotation}
                tables={tables}
                index={index}
                onDelete={handleAnnotationDelete}
              />
            ))}
          </Box>
        )}
      </Paper>

      <AnnotationModal
        open={modalOpen}
        annotation={pendingAnnotation}
        onClose={handleModalClose}
        onConfirm={handleAnalyzeConfirm}
        onRetry={analysisError ? retryAnalysis : undefined}
        isAnalyzing={isAnalyzing}
        error={analysisError}
      />
    </Box>
  );
};